'use client';

import FormInput, { InputProps } from '@/components/Form/FormInput';
import { formErrorKeyMap, removeEmoji } from '@/utils/form-rule';
import { useTranslations } from 'next-intl';
import React from 'react';

// TaxIDInput组件的属性，继承自FormInput的属性
export type TaxIDInputProps = Omit<InputProps, 'formatInput'> & {
  countryId: string;
};

const TaxIDInput: React.FC<TaxIDInputProps> = ({
  countryId,
  placeholder,
  label,
  slotProps,
  layout = 'inline',
  required = true,
  ...restProps
}) => {
  const t = useTranslations('components.address.validation');
  const tTaxInput = useTranslations('components.form.taxIdInput');

  // 验证墨西哥税号
  const isMXCPF = (val: string): boolean => {
    // 格式1: 13位
    const format1 =
      /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[A-Z0-9]{2}[0-9A-Z]$/;

    // 格式2: 18位
    const format2 =
      /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}(?:[0-9]|[A-Z])[0-9]$/;

    return format1.test(val) || format2.test(val);
  };

  // 格式化税号
  const formatTaxID = (value: string): string => {
    let taxId = removeEmoji(value);

    if (countryId === 'BR') {
      // 巴西税号格式化
      taxId = taxId.replaceAll('.', '');
      taxId = taxId.replaceAll('-', '');
      taxId = taxId.replaceAll('/', '');
      const valTemp = taxId;

      if (valTemp.length <= 11) {
        if (taxId.length >= 4) {
          taxId = `${taxId.substring(0, 3)}.${taxId.substring(3)}`;
        }
        if (taxId.length >= 8) {
          taxId = `${taxId.substring(0, 7)}.${taxId.substring(7)}`;
        }
        if (taxId.length >= 12) {
          taxId = `${taxId.substring(0, 11)}-${taxId.substring(11)}`;
        }
      } else {
        taxId = taxId.substring(0, 14);
      }
    } else if (countryId === 'MX') {
      // 墨西哥税号格式化
      taxId = taxId.toUpperCase();
      taxId = taxId.replace(/[^a-zA-Z0-9]/g, '');
    }

    return taxId.trim();
  };

  // 验证规则
  const taxIDRules = {
    required: required
      ? tTaxInput(`validation.${countryId === 'BR' ? 'brazilRequired' : 'mexicoRequired'}`)
      : false,
    validate: (value: string) => {
      if (!required && (!value || value.trim() === '')) {
        return true;
      }

      // 验证税号
      if (countryId === 'BR') {
        if (
          !(
            /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) ||
            /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value) ||
            /^\d{11}$/g.test(value) ||
            /^\d{14}$/g.test(value)
          )
        ) {
          return t(formErrorKeyMap[17]); // 巴西税号格式错误
        }
      } else if (countryId === 'MX') {
        if (!isMXCPF(value)) {
          return t(formErrorKeyMap[25]); // 墨西哥税号格式错误
        }
      }

      return true;
    },
  };

  return (
    <FormInput
      label={label || tTaxInput('label')}
      layout={layout}
      placeholder={placeholder || tTaxInput('placeholder')}
      required={required}
      formatInput={formatTaxID}
      rules={taxIDRules}
      {...restProps}
      slotProps={{
        htmlInput: {
          maxLength: countryId === 'MX' ? 18 : undefined,
        },
        ...slotProps,
      }}
    />
  );
};

export default TaxIDInput;
